<template>
  <div class="relative">
    <!-- Mobile Full-Screen Overlay -->
    <div v-if="showMobileOverlay" class="fixed inset-0 z-50 bg-white md:hidden flex flex-col">
      <!-- Top Search Bar -->
      <div class="flex items-center p-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex-1 relative">
          <input
            ref="mobileSearchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Search shows..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-gray-100 text-black"
            @input="handleSearch"
            @keyup.escape="closeMobileOverlay"
            @keydown="handleKeydown"
          />
          <svg
            class="absolute ml-3 top-0 mt-3 h-5 w-5 text-gray-400 pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          @click="closeMobileOverlay"
          class="ml-4 text-orange-500 font-medium text-base"
        >
          Cancel
        </button>
      </div>

      <!-- Search Results -->
      <div class="flex-1 overflow-y-auto" style="min-height: 0; scroll-behavior: smooth;">
        <!-- Tabs -->
        <div v-if="shouldShowTabs" class="border-b border-gray-200 bg-white sticky top-0">
          <div class="flex">
            <button
              @click="switchTab('shows')"
              :class="{
                'text-gray-900 border-b-2 border-gray-900': activeTab === 'shows',
                'text-gray-600 hover:text-gray-800 border-b-2 border-transparent': activeTab !== 'shows'
              }"
              class="flex-1 px-4 py-3 text-sm font-medium focus:outline-none"
            >
              Shows
              <span v-if="filteredEvents.length > 0" class="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                {{ filteredEvents.length }}
              </span>
            </button>
            <button
              @click="switchTab('series')"
              :class="{
                'text-gray-900 border-b-2 border-gray-900': activeTab === 'series',
                'text-gray-600 hover:text-gray-800 border-b-2 border-transparent': activeTab !== 'series'
              }"
              class="flex-1 px-4 py-3 text-sm font-medium focus:outline-none"
            >
              Show Series
              <span v-if="filteredSeries.length > 0" class="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                {{ filteredSeries.length }}
              </span>
            </button>
          </div>
        </div>

        <!-- No results state -->
        <div v-if="!shouldShowTabs && searchQuery.length > 0" class="p-6 text-center text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <div class="text-sm font-medium text-gray-700 mb-1">No shows or series found</div>
          <div class="text-xs text-gray-500">Try a different search term</div>
        </div>

        <!-- Tab Content -->
        <div v-if="shouldShowTabs">
          <!-- Shows Tab -->
          <div v-if="activeTab === 'shows'">
            <!-- No shows results -->
            <div v-if="filteredEvents.length === 0 && dedupedPastEvents.length === 0" class="p-6 text-center text-gray-500">
              <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <div class="text-sm font-medium text-gray-700 mb-1">No shows found</div>
              <div class="text-xs text-gray-500">Try the Series tab or a different search term</div>
            </div>

            <!-- Shows Results -->
            <div v-else class="divide-y divide-gray-100">
              <div class="px-4 py-2 bg-gray-100" v-if="filteredEvents.length">
                <h3 class="text-sm font-medium text-gray-700">Upcoming Shows</h3>
              </div>
              <div v-else class="p-2 text-center text-gray-500">
                <div class="text-sm font-medium text-gray-700 mb-1">No upcoming shows found</div>
              </div>
              <div
                v-for="(event, index) in filteredEvents"
                :key="event.id || event.event_identifier"
                @click="selectEventMobile(event)"
                @touchstart="selectedIndex = index"
                :class="{
                  'bg-gray-200': selectedIndex === index,
                  'hover:bg-gray-50': selectedIndex !== index
                }"
                class="flex items-center py-3 px-4 cursor-pointer"
              >
                <!-- Event Cover Photo -->
                <div class="flex-shrink-0 w-20 h-12 mr-4 rounded overflow-hidden bg-gray-200">
                  <img
                    :src="getEventCoverPhoto(event)"
                    :alt="`${event.name} cover photo`"
                    class="w-full h-full object-cover"
                    @error="handleImageError"
                  />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center space-x-2 justify-between mb-1">
                    <div class="font-semibold text-gray-900 text-base">
                      {{ event.name }}
                    </div>
                    <!-- Status Badge -->
                    <span
                      v-if="getEventStatusBadge(event)"
                      :class="getEventStatusBadge(event).class"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0"
                    >
                      {{ getEventStatusBadge(event).text }}
                    </span>
                  </div>
                  <div class="flex items-center space-x-2 text-sm text-gray-600">
                    <span v-if="event.start_date">{{ formatDate(event.start_date) }}</span>
                    <span v-if="event.start_date && event.location_details">•</span>
                    <span v-if="event.location_details">
                      {{ event.location_details.city }}, {{ event.location_details.state }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Explore all Shows option -->
              <div v-if="!isOnShowsPage && searchQuery" class="border-t border-gray-100">
                <div
                  @click="exploreAllShowsMobile"
                  class="flex items-center py-2 px-4 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div class="flex-shrink-0 w-10 h-10 mr-4 flex items-center justify-center rounded-lg bg-gray-100">
                    <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="font-medium text-gray-700 text-sm">Explore all Shows</div>
                    <div class="text-xs text-gray-600">Browse all upcoming livestock shows</div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Past Shows Section -->
              <div v-if="dedupedPastEvents.length > 0" class="border-t border-gray-200">
                <div class="px-4 py-2 bg-gray-100">
                  <h3 class="text-sm font-medium text-gray-700">Past Shows</h3>
                </div>
                <div
                  v-for="(event, index) in dedupedPastEvents"
                  :key="event.event_id || event.event_identifier"
                  @click="selectPastEventMobile(event)"
                  @touchstart="selectedIndex = filteredEvents.length + index"
                  :class="{
                    'bg-gray-200': selectedIndex === filteredEvents.length + index,
                    'hover:bg-gray-50': selectedIndex !== filteredEvents.length + index
                  }"
                  class="flex items-center py-3 px-4 cursor-pointer"
                >
                  <!-- Event Cover Photo -->
                  <div class="flex-shrink-0 w-20 h-10 mr-4 rounded overflow-hidden bg-gray-200">
                    <img
                      :src="getPastEventCoverPhoto(event)"
                      :alt="`${event.name} cover photo`"
                      class="w-full h-full object-cover"
                      @error="handlePastEventImageError"
                    />
                  </div>

                  <!-- Content -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2 mb-1 justify-between">
                      <div class="font-semibold text-gray-900 text-base">
                        {{ event.name }}
                      </div>
                      <!-- Badges -->
                      <div class="flex items-center space-x-1 flex-shrink-0">
                        <!-- Status Badge -->
                        <span
                          v-if="getEventStatusBadge(event)"
                          :class="getEventStatusBadge(event).class"
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                        >
                          {{ getEventStatusBadge(event).text }}
                        </span>
                        <!-- Past Event Badge -->
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-blue-200 bg-blue-100 text-gray-700">
                          Past
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center space-x-2 text-sm text-gray-600">
                      <span v-if="event.start_date">{{ formatDate(event.start_date) }}</span>
                      <span v-if="event.start_date && event.city">•</span>
                      <span v-if="event.city && event.state">
                        {{ event.city }}, {{ event.state }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Series Tab -->
          <div v-if="activeTab === 'series'">
            <!-- No series results -->
            <div v-if="filteredSeries.length === 0" class="p-6 text-center text-gray-500">
              <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <div class="text-sm font-medium text-gray-700 mb-1">No series found</div>
              <div class="text-xs text-gray-500">Try the Shows tab or a different search term</div>
            </div>

            <!-- Series Results -->
            <div v-else class="divide-y divide-gray-100">
              <div
                v-for="(seriesItem, index) in filteredSeries"
                :key="seriesItem.show_series_id"
                @click="selectSeriesMobile(seriesItem)"
                @touchstart="selectedIndex = index"
                :class="{
                  'bg-gray-200': selectedIndex === index,
                  'hover:bg-gray-50': selectedIndex !== index
                }"
                class="flex items-center py-3 px-4 cursor-pointer"
              >
                <!-- Series Logo -->
                <div class="flex-shrink-0 w-12 h-12 mr-4 rounded-lg overflow-hidden bg-gray-200">
                  <img
                    :src="getSeriesLogo(seriesItem)"
                    :alt="`${seriesItem.name} logo`"
                    class="w-full h-full object-cover"
                    @error="handleSeriesImageError"
                  />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="font-semibold text-gray-900 text-base mb-1">
                    {{ seriesItem.name }}
                  </div>
                  <div class="text-sm text-gray-600">
                    {{ seriesItem.organization }}
                  </div>
                  <div v-if="seriesItem.series_details && seriesItem.series_details.year" class="text-xs text-gray-500 mt-1">
                    {{ seriesItem.series_details.year }}
                  </div>
                </div>
              </div>

              <!-- Explore all Series option -->
              <div class="border-t border-gray-100">
                <div
                  @click="exploreAllSeriesMobile"
                  class="flex items-center py-2 px-4 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div class="flex-shrink-0 w-10 h-10 mr-4 flex items-center justify-center rounded-lg bg-gray-100">
                    <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="font-medium text-gray-700 text-sm">Explore all Series</div>
                    <div class="text-xs text-gray-600">Browse all show series</div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state when no search query -->
        <div v-if="searchQuery.length === 0" class="p-6 text-center text-gray-500">
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <div class="text-lg font-medium text-gray-700 mb-2">Search by location</div>
          <div class="text-sm text-gray-500">Search by city, show name, or state</div>
        </div>
      </div>
    </div>

    <!-- Desktop/Default Search Bar -->
    <div class="relative" :class="{ 'md:block hidden': showMobileOverlay }">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search shows..."
        class="w-full pl-8 md:pl-10 pr-4 py-1.5 md:py-3 border-2 text-black border-orange-200_ rounded-xl md:rounded-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-gray-200 text-base"
        @input="handleSearch"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />
      <svg
        class="absolute left-0 top-0 mt-3 ml-2 md:ml-3 h-4 w-4 md:h-5 md:w-5 text-orange-400 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>

    <!-- Desktop Dropdown -->
    <div
      v-if="showDropdown && !isMobile && (shouldShowTabs || searchQuery.length > 0 || (!isOnShowsPage && searchQuery.length === 0))"
      class="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden w-full left-0"
    >
      <!-- Tabs -->
      <div v-if="shouldShowTabs" class="border-b border-gray-200">
        <div class="flex">
          <button
            @mousedown.prevent="switchTab('shows')"
            :class="{
              'text-gray-900 border-b-2 border-gray-900': activeTab === 'shows',
              'text-gray-600 hover:text-gray-800 border-b-2 border-transparent': activeTab !== 'shows'
            }"
            class="flex-1 px-4 py-3 text-sm font-medium focus:outline-none"
          >
            Shows
            <span v-if="filteredEvents.length > 0" class="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
              {{ filteredEvents.length }}
            </span>
          </button>
          <button
            @mousedown.prevent="switchTab('series')"
            :class="{
              'text-gray-900 border-b-2 border-gray-900': activeTab === 'series',
              'text-gray-600 hover:text-gray-800 border-b-2 border-transparent': activeTab !== 'series'
            }"
            class="flex-1 px-4 py-3 text-sm font-medium focus:outline-none"
          >
            Show Series
            <span v-if="filteredSeries.length > 0" class="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
              {{ filteredSeries.length }}
            </span>
          </button>
        </div>
      </div>

      <!-- No results state -->
      <div v-if="!shouldShowTabs && searchQuery.length > 0" class="p-6 text-center text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <div class="text-sm font-medium text-gray-700 mb-1">No shows or series found</div>
        <div class="text-xs text-gray-500">Try a different search term</div>
      </div>

      <!-- Tab Content -->
      <div v-if="shouldShowTabs" class="overflow-y-auto relative" style="max-height: 45rem; min-height: 35rem; scroll-behavior: smooth;">

        <!-- Shows Tab -->
        <div v-if="activeTab === 'shows'">
          <!-- No shows results -->
          <div v-if="filteredEvents.length === 0 && dedupedPastEvents.length === 0" class="p-6 text-center text-gray-500">
            <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <div class="text-sm font-medium text-gray-700 mb-1">No shows found</div>
            <div class="text-xs text-gray-500">Try the Series tab or a different search term</div>
          </div>

          <!-- Shows Results -->
          <div v-else>
            <div class="px-4 py-2 bg-gray-100" v-if="filteredEvents.length">
              <h3 class="text-sm font-medium text-gray-700">Upcoming Shows</h3>
            </div>
            <div v-else class="p-2 text-center text-gray-500">
                <div class="text-sm font-medium text-gray-700 mb-1">No upcoming shows found</div>
              </div>
            <div
              v-for="(event, index) in filteredEvents"
              :key="event.id || event.event_identifier"
              @mousedown="selectEvent(event)"
              @mouseenter="selectedIndex = index"
              :class="{
                'bg-gray-200': selectedIndex === index,
                'hover:bg-gray-100': selectedIndex !== index
              }"
              class="flex items-center py-2 px-4 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <!-- Event Cover Photo -->
              <div class="flex-shrink-0 w-20 h-10 mr-4 rounded overflow-hidden bg-gray-200">
                <img
                  :src="getEventCoverPhoto(event)"
                  :alt="`${event.name} cover photo`"
                  class="w-full h-full object-cover"
                  @error="handleImageError"
                />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0 mr-4">
                <div class="flex items-center space-x-2 justify-between mb-1">
                  <div class="font-semibold text-gray-900 truncate text-base">
                    {{ event.name }}
                  </div>
                  <!-- Status Badge -->
                  <span
                    v-if="getEventStatusBadge(event)"
                    :class="getEventStatusBadge(event).class"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0"
                  >
                    {{ getEventStatusBadge(event).text }}
                  </span>
                </div>
                <div class="flex space-x-1.5 items-center">
                  <div v-if="event.start_date" class="text-sm text-gray-600 mt-1">
                    {{ formatDate(event.start_date) }}
                  </div>
                  <div v-if="event.location_details" class="text-sm text-gray-600 truncate mt-1">
                    {{ event.location_details.city }}, {{ event.location_details.state }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Explore all Shows option -->
            <div v-if="!isOnShowsPage && searchQuery" class="border-t border-gray-100">
              <div
                @mousedown="exploreAllShows"
                class="flex items-center py-2 px-4 hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <div class="flex-shrink-0 w-8 h-8 mr-4 flex items-center justify-center rounded-lg bg-gray-100">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="font-medium text-gray-700 text-sm">Explore all Shows</div>
                  <div class="text-xs text-gray-600">Browse all upcoming livestock shows</div>
                </div>
                <div class="flex-shrink-0">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Past Shows Section -->
            <div v-if="dedupedPastEvents.length > 0" class="border-t border-gray-200">
              <div class="px-4 py-2 bg-gray-100">
                <h3 class="text-sm font-medium text-gray-700">Past Shows</h3>
              </div>
              <div
                v-for="(event, index) in dedupedPastEvents"
                :key="event.event_id || event.event_identifier"
                @mousedown="selectPastEvent(event)"
                @mouseenter="selectedIndex = filteredEvents.length + index"
                :class="{
                  'bg-gray-200': selectedIndex === filteredEvents.length + index,
                  'hover:bg-gray-100': selectedIndex !== filteredEvents.length + index
                }"
                class="flex items-center py-2 px-4 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <!-- Event Cover Photo -->
                <div class="flex-shrink-0 w-20 h-10 mr-4 rounded overflow-hidden bg-gray-200">
                  <img
                    :src="getPastEventCoverPhoto(event)"
                    :alt="`${event.name} cover photo`"
                    class="w-full h-full object-cover"
                    @error="handlePastEventImageError"
                  />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0 mr-4">
                  <div class="flex items-center space-x-2 justify-between">
                    <div class="font-semibold text-gray-900 truncate text-base">
                      {{ event.name }}
                    </div>
                    <!-- Badges -->
                    <div class="flex items-center space-x-1 flex-shrink-0">
                      <!-- Status Badge -->
                      <span
                        v-if="getEventStatusBadge(event)"
                        :class="getEventStatusBadge(event).class"
                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                      >
                        {{ getEventStatusBadge(event).text }}
                      </span>
                      <!-- Past Event Badge -->
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Past
                      </span>
                    </div>
                  </div>
                  <div class="flex space-x-1.5 items-center">
                    <div v-if="event.start_date" class="text-sm text-gray-500 mt-1">
                      {{ formatDate(event.start_date) }}
                    </div>
                    <div v-if="event.city && event.state" class="text-sm text-gray-600 truncate mt-1">
                      {{ event.city }}, {{ event.state }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Series Tab -->
        <div v-if="activeTab === 'series'">
          <!-- No series results -->
          <div v-if="filteredSeries.length === 0" class="p-6 text-center text-gray-500">
            <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <div class="text-sm font-medium text-gray-700 mb-1">No series found</div>
            <div class="text-xs text-gray-500">Try the Shows tab or a different search term</div>
          </div>

          <!-- Series Results -->
          <div v-else>
            <div
              v-for="(seriesItem, index) in filteredSeries"
              :key="seriesItem.show_series_id"
              @mousedown="selectSeries(seriesItem)"
              @mouseenter="selectedIndex = index"
              :class="{
                'bg-gray-200': selectedIndex === index,
                'hover:bg-gray-100': selectedIndex !== index
              }"
              class="flex items-center py-2 px-4 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <!-- Series Logo -->
              <div class="flex-shrink-0 w-10 h-10 mr-4 rounded-lg overflow-hidden bg-gray-200">
                <img
                  :src="getSeriesLogo(seriesItem)"
                  :alt="`${seriesItem.name} logo`"
                  class="w-full h-full object-cover"
                  @error="handleSeriesImageError"
                />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0 mr-4">
                <div class="font-semibold text-gray-900 truncate text-base">
                  {{ seriesItem.name }}
                </div>
                <div class="text-sm text-gray-600 truncate mt-1">
                  {{ seriesItem.organization }}
                </div>
                <div v-if="seriesItem.series_details && seriesItem.series_details.year" class="text-xs text-gray-500 mt-1">
                  {{ seriesItem.series_details.year }}
                </div>
              </div>
            </div>

            <!-- Explore all Series option -->
            <div class="border-t border-gray-100">
              <div
                @mousedown="exploreAllSeries"
                class="flex items-center py-2 px-4 hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <div class="flex-shrink-0 w-8 h-8 mr-4 flex items-center justify-center rounded-lg bg-gray-100">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="font-medium text-gray-700 text-sm">Explore all Series</div>
                  <div class="text-xs text-gray-600">Browse all show series</div>
                </div>
                <div class="flex-shrink-0">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll indicator gradient -->
      <div v-if="shouldShowTabs" class="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>

      <!-- Empty state when no search query -->
      <div v-if="searchQuery.length === 0 && !shouldShowTabs" class="p-6 text-center text-gray-500">
        <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <div class="text-lg font-medium text-gray-700 mb-2">Search by location</div>
        <div class="text-sm text-gray-500">Search by city, show name, or state</div>
      </div>
    </div>
  </div>
</template>

<script>
import {cldUrl} from '@/cloudinaryUrl'
import UserAccess from '@/userAccessLevel'

export default {
  name: 'ShowSearchBar',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    series: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      searchQuery: '',
      showDropdown: false,
      filteredEvents: [],
      filteredSeries: [],
      pastEvents: [],
      showMobileOverlay: false, // New state for mobile overlay
      isMobile: false, // New state for mobile detection
      selectedIndex: -1, // Index of currently selected item (-1 means no selection)
      activeTab: 'shows', // 'shows' or 'series'
      pastEventsSearchTimeout: null // For debouncing past events search
    }
  },
  computed: {
    // Check if we're currently on the shows page (home route)
    isOnShowsPage () {
      return this.$route.name === 'home' || this.$route.path === '/'
    },
    currentUser () {
      return this.$store.state.user
    },
    // Check if current user is an admin
    isAdminUser () {
      if (this.currentUser) {
        return UserAccess.checkUserAccess(this.currentUser.user_id, 'SUPPORT')
      } else {
        return false
      }
    },
    // Current tab results for keyboard navigation
    currentTabResults () {
      if (this.activeTab === 'shows') {
        const events = this.filteredEvents.map(event => ({type: 'event', data: event}))
        const pastEvents = this.dedupedPastEvents.map(event => ({type: 'pastEvent', data: event}))
        return [...events, ...pastEvents]
      } else {
        return this.filteredSeries.map(series => ({type: 'series', data: series}))
      }
    },
    // Combined list of all searchable items for keyboard navigation (legacy support)
    allSelectableItems () {
      return this.currentTabResults
    },
    // Maximum selectable index
    maxSelectableIndex () {
      return Math.max(0, this.currentTabResults.length - 1)
    },
    // Currently selected item
    selectedItem () {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.currentTabResults.length) {
        return this.currentTabResults[this.selectedIndex]
      }
      return null
    },
    // Whether we have any results to navigate in current tab
    hasResults () {
      return this.currentTabResults.length > 0
    },
    // Whether to show tabs
    shouldShowTabs () {
      return this.filteredEvents.length > 0 || this.filteredSeries.length > 0 || this.dedupedPastEvents.length > 0
    },
    // Past results with any show already listed under "Upcoming Shows" removed.
    // A show happening today is returned by both the upcoming feed and the past
    // search; keep it only in Upcoming so it doesn't appear twice.
    dedupedPastEvents () {
      const upcomingIds = new Set(
        this.filteredEvents.map(e => e.event_identifier || e.id)
      )
      return this.pastEvents.filter(e => !upcomingIds.has(e.event_identifier || e.event_id))
    }
  },
  watch: {
    $route () {
      // Close mobile overlay when route changes
      this.closeMobileOverlay()
      // Reset all search state when navigating
      this.resetSearchState()
    },
    // Reset selection when search results change
    allSelectableItems () {
      this.selectedIndex = -1
    },
    // Reset selection when switching tabs
    activeTab () {
      this.selectedIndex = -1
    }
  },
  mounted () {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkIsMobile)
    if (this.pastEventsSearchTimeout) {
      clearTimeout(this.pastEventsSearchTimeout)
    }
  },
  methods: {
    checkIsMobile () {
      this.isMobile = window.innerWidth < 768 // md breakpoint
    },
    handleSearch () {
      // If completely empty, clear results
      if (this.searchQuery.length === 0) {
        this.filteredEvents = []
        this.filteredSeries = []
        this.pastEvents = []
        return
      }

      // Normalize the search query: trim whitespace and collapse multiple spaces
      const trimmedQuery = this.searchQuery.trim()

      // If whitespace-only (e.g., " "), show all results
      const showAll = trimmedQuery.length === 0

      // Normalize: lowercase and collapse multiple spaces into single space
      const query = trimmedQuery.toLowerCase().replace(/\s+/g, ' ')

      // Search events from props (published upcoming events)
      const allFilteredEvents = this.events.filter(event => {
        // If showing all, include every event
        if (showAll) return true

        // Normalize event name for comparison
        const normalizedEventName = event.name ? event.name.toLowerCase().trim() : ''
        const nameMatch = normalizedEventName.includes(query)

        // Normalize city for comparison
        const normalizedCity = event.location_details && event.location_details.city
          ? event.location_details.city.toLowerCase().trim()
          : ''
        const cityMatch = normalizedCity.includes(query)

        // Check both full state name and abbreviation
        const stateMatch = this.matchesState(event, query)

        return nameMatch || cityMatch || stateMatch
      })

      // Search series
      const allFilteredSeries = this.series.filter(seriesItem => {
        // If showing all, include every series
        if (showAll) return true

        // Normalize series name for comparison
        const normalizedSeriesName = seriesItem.name ? seriesItem.name.toLowerCase().trim() : ''
        const nameMatch = normalizedSeriesName.includes(query)

        // Normalize organization for comparison
        const normalizedOrg = seriesItem.organization ? seriesItem.organization.toLowerCase().trim() : ''
        const orgMatch = normalizedOrg.includes(query)

        return nameMatch || orgMatch
      })

      // Apply different limits based on mobile/desktop
      if (this.isMobile) {
        // Mobile: Show top 9 results total, prioritizing events
        const totalResults = [...allFilteredEvents, ...allFilteredSeries].slice(0, 7)
        this.filteredEvents = totalResults.filter(item => item.location_details || item.start_date)
        this.filteredSeries = this.filteredSeries = allFilteredSeries.slice(0, 8)
      } else {
        // Desktop: Show more results in separate tabs
        this.filteredEvents = allFilteredEvents.slice(0, 10)
        this.filteredSeries = allFilteredSeries.slice(0, 8)
      }

      // Always show dropdown when handling search (unless on mobile)
      if (!this.isMobile) {
        this.showDropdown = true
      }

      // Background: fetch past events and unpublished upcoming events for the query with debouncing and 2-char minimum
      this.debouncedFetchAdditionalEvents(this.searchQuery)
    },
    debouncedFetchAdditionalEvents (searchText) {
      // Clear any existing timeout
      if (this.pastEventsSearchTimeout) {
        clearTimeout(this.pastEventsSearchTimeout)
      }

      // Require at least 2 characters
      const q = (searchText || '').trim()
      if (q.length < 2) {
        this.pastEvents = []
        return
      }

      // Set a 300ms delay to wait for user to stop typing
      this.pastEventsSearchTimeout = setTimeout(() => {
        this.fetchAdditionalEvents(q)
      }, 300)
    },
    fetchAdditionalEvents (searchText) {
      // Guard against empty or whitespace-only queries
      const q = (searchText || '').trim()
      if (!q) {
        this.pastEvents = []
        return
      }

      // Use the shared API instance from the Vuex store
      const api = this.$store && this.$store.state && this.$store.state.apiInstance
      if (!api) {
        return
      }

      // Use admin endpoint if user is admin, otherwise use standard endpoint
      const endpoint = this.isAdminUser
        ? `/adminEventSearch?search_text=${encodeURIComponent(q)}`
        : `/standardEventSearchPast?search_text=${encodeURIComponent(q)}`

      api.get(endpoint)
        .then(response => {
          // Expected response: array of events (city, name, state, event_id, start_date, event_card_cover_url, event_identifier)
          let events = []
          if (response && Array.isArray(response.data)) {
            events = response.data
          } else if (response && response.data && Array.isArray(response.data.data)) {
            // Fallback if API nests data under .data
            events = response.data.data
          }

          if (this.isAdminUser) {
            // For admin users, categorize events into past and upcoming unpublished
            const today = new Date()
            today.setHours(0, 0, 0, 0) // Start of today

            const pastEvents = []
            const upcomingUnpublishedEvents = []

            events.forEach(event => {
              if (event.start_date) {
                const eventDate = new Date(event.start_date)
                eventDate.setHours(0, 0, 0, 0) // Start of event date

                if (eventDate < today) {
                  pastEvents.push(event)
                } else {
                  // This is an upcoming unpublished event - add to filteredEvents
                  upcomingUnpublishedEvents.push(event)
                }
              } else {
                // If no start_date, treat as past event
                pastEvents.push(event)
              }
            })

            this.pastEvents = pastEvents

            // Add upcoming unpublished events to the filtered events list
            // Remove duplicates by checking if event already exists in filteredEvents
            const existingEventIds = new Set(this.filteredEvents.map(e => e.id || e.event_identifier))
            const newUpcomingEvents = upcomingUnpublishedEvents.filter(event =>
              !existingEventIds.has(event.id || event.event_identifier)
            )

            // Merge with existing filtered events and apply limits
            const allUpcomingEvents = [...this.filteredEvents, ...newUpcomingEvents]
            if (this.isMobile) {
              this.filteredEvents = allUpcomingEvents.slice(0, 7)
            } else {
              this.filteredEvents = allUpcomingEvents.slice(0, 10)
            }
          } else {
            // For regular users, all results are past events
            this.pastEvents = events
          }
        })
        .catch(() => {
          // Silently fail; keep UI responsive
          this.pastEvents = []
        })
    },
    handleKeydown (event) {
      // Handle Tab key for switching between tabs (works when dropdown is open)
      if (event.key === 'Tab' && this.showDropdown && this.shouldShowTabs) {
        event.preventDefault()
        this.switchToNextTab()
        return
      }

      // Only handle keyboard navigation if dropdown is open and we have results
      if (!this.showDropdown || !this.hasResults) {
        return
      }

      switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateUp()
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentItem()
        break
      case 'Escape':
        event.preventDefault()
        this.closeDropdown()
        break
      }
    },
    navigateDown () {
      if (!this.hasResults) return

      if (this.selectedIndex < this.maxSelectableIndex) {
        this.selectedIndex++
      } else {
        // Wrap to first item
        this.selectedIndex = 0
      }
    },
    navigateUp () {
      if (!this.hasResults) return

      if (this.selectedIndex > 0) {
        this.selectedIndex--
      } else if (this.selectedIndex === -1) {
        // Start from last item when navigating up from no selection
        this.selectedIndex = this.maxSelectableIndex
      } else {
        // Wrap to last item
        this.selectedIndex = this.maxSelectableIndex
      }
    },
    selectCurrentItem () {
      if (this.selectedItem) {
        if (this.selectedItem.type === 'event') {
          this.selectEvent(this.selectedItem.data)
        } else if (this.selectedItem.type === 'series') {
          this.selectSeries(this.selectedItem.data)
        } else if (this.selectedItem.type === 'pastEvent') {
          this.selectPastEvent(this.selectedItem.data)
        }
      }
    },
    closeDropdown () {
      this.showDropdown = false
      this.selectedIndex = -1
    },
    switchTab (tabName) {
      this.activeTab = tabName
    },
    switchToNextTab () {
      // Cycle between shows and series tabs
      if (this.activeTab === 'shows') {
        this.activeTab = 'series'
      } else {
        this.activeTab = 'shows'
      }
    },
    resetSearchState () {
      // Reset all search-related state to ensure clean functionality
      this.showDropdown = false
      this.selectedIndex = -1
      this.filteredEvents = []
      this.filteredSeries = []
      // Don't reset searchQuery as user might want to keep their search term
    },
    matchesState (event, query) {
      if (!event.location_details || !event.location_details.state) {
        return false
      }

      const stateOnEvent = event.location_details.state // This might be abbreviation or full name
      const queryLower = query.toLowerCase()

      // Check for exact match with what's stored on the event (could be abbreviation or full name)
      if (stateOnEvent.toLowerCase() === queryLower) {
        return true
      }

      // If what's stored is an abbreviation, get the full name and compare
      const fullStateName = this.getFullStateNameFromAbbr(stateOnEvent)
      if (fullStateName && fullStateName.toLowerCase() === queryLower) {
        return true
      }

      // If what's stored is a full name, get the abbreviation and compare
      const abbreviation = this.getKnownStateAbbreviation(stateOnEvent)
      if (abbreviation && abbreviation.toLowerCase() === queryLower) {
        return true
      }

      return false
    },
    getKnownStateAbbreviation (stateName) {
      const stateAbbreviations = {
        'alabama': 'AL',
        'alaska': 'AK',
        'arizona': 'AZ',
        'arkansas': 'AR',
        'california': 'CA',
        'colorado': 'CO',
        'connecticut': 'CT',
        'delaware': 'DE',
        'florida': 'FL',
        'georgia': 'GA',
        'hawaii': 'HI',
        'idaho': 'ID',
        'illinois': 'IL',
        'indiana': 'IN',
        'iowa': 'IA',
        'kansas': 'KS',
        'kentucky': 'KY',
        'louisiana': 'LA',
        'maine': 'ME',
        'maryland': 'MD',
        'massachusetts': 'MA',
        'michigan': 'MI',
        'minnesota': 'MN',
        'mississippi': 'MS',
        'missouri': 'MO',
        'montana': 'MT',
        'nebraska': 'NE',
        'nevada': 'NV',
        'new hampshire': 'NH',
        'new jersey': 'NJ',
        'new mexico': 'NM',
        'new york': 'NY',
        'north carolina': 'NC',
        'north dakota': 'ND',
        'ohio': 'OH',
        'oklahoma': 'OK',
        'oregon': 'OR',
        'pennsylvania': 'PA',
        'rhode island': 'RI',
        'south carolina': 'SC',
        'south dakota': 'SD',
        'tennessee': 'TN',
        'texas': 'TX',
        'utah': 'UT',
        'vermont': 'VT',
        'virginia': 'VA',
        'washington': 'WA',
        'west virginia': 'WV',
        'wisconsin': 'WI',
        'wyoming': 'WY',
        // Canadian provinces
        'ontario': 'ON',
        'british columbia': 'BC',
        'alberta': 'AB',
        'saskatchewan': 'SK',
        'manitoba': 'MB',
        'quebec': 'QC',
        'new brunswick': 'NB',
        'nova scotia': 'NS',
        'prince edward island': 'PE',
        'newfoundland and labrador': 'NL',
        'northwest territories': 'NT',
        'nunavut': 'NU',
        'yukon': 'YT'
      }

      return stateAbbreviations[stateName.toLowerCase()] || null
    },
    selectEvent (event) {
      this.searchQuery = event.name
      this.showDropdown = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', event)
    },
    selectSeries (seriesItem) {
      this.searchQuery = seriesItem.name
      this.showDropdown = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', seriesItem)
    },
    exploreAllShows () {
      this.searchQuery = ''
      this.showDropdown = false
      // Navigate to home page which shows all upcoming shows
      this.$router.push('/')
    },
    exploreAllSeries () {
      this.searchQuery = ''
      this.showDropdown = false
      // Navigate to show series page
      this.$router.push('/show-series')
    },
    handleBlur () {
      // Delay hiding dropdown to allow click events to register
      setTimeout(() => {
        this.showDropdown = false
      }, 150)
    },
    formatDate (dateString) {
      if (!dateString) return ''

      // Parse the date as local time to avoid timezone offset issues
      const dateParts = dateString.split('-')
      if (dateParts.length === 3) {
        // Create date using local timezone (month is 0-indexed)
        const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      }

      // Fallback for other date formats
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    getEventCoverPhoto (event) {
      // Check for cover photo in event details
      if (event.event_details && event.event_details.event_card_cover_url) {
        return event.event_details.event_card_cover_url
      } else if (event.event_card_cover_url) {
        return event.event_card_cover_url
      }

      // Fallback to generated default cover photo similar to other components
      if (event.name) {
        return `https://res.cloudinary.com/showman/image/upload/c_fill,g_auto,w_480,ar_16:7,q_auto,f_auto/c_scale,co_rgb:ffffff,l_text:montserrat_50_align_center_style_bold:${encodeURIComponent(event.name)},g_center,w_400,c_fit/v1620138098/defaultPhotos/Blank_Showman_Orange.png`
      }

      // Final fallback
      return 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    handleImageError (e) {
      // Fallback to a simple default image if the cover photo fails to load
      e.target.src = 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    getPastEventCoverPhoto (event) {
      // Check for cover photo in past event data
      if (event.event_card_cover_url) {
        return event.event_card_cover_url
      }

      // Fallback to generated default cover photo
      if (event.name) {
        return `https://res.cloudinary.com/showman/image/upload/c_fill,g_auto,w_480,ar_16:7,q_auto,f_auto/c_scale,co_rgb:ffffff,l_text:montserrat_50_align_center_style_bold:${encodeURIComponent(event.name)},g_center,w_400,c_fit/v1620138098/defaultPhotos/Blank_Showman_Orange.png`
      }

      // Final fallback
      return 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    handlePastEventImageError (e) {
      // Fallback to a simple default image if the past event cover photo fails to load
      e.target.src = 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    selectPastEvent (event) {
      this.searchQuery = event.name
      this.showDropdown = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', event)
    },
    selectPastEventMobile (event) {
      this.searchQuery = event.name
      this.showMobileOverlay = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', event)
    },
    getFullStateNameFromAbbr (abbreviation) {
      const abbrToFullName = {
        'AL': 'Alabama',
        'AK': 'Alaska',
        'AZ': 'Arizona',
        'AR': 'Arkansas',
        'CA': 'California',
        'CO': 'Colorado',
        'CT': 'Connecticut',
        'DE': 'Delaware',
        'FL': 'Florida',
        'GA': 'Georgia',
        'HI': 'Hawaii',
        'ID': 'Idaho',
        'IL': 'Illinois',
        'IN': 'Indiana',
        'IA': 'Iowa',
        'KS': 'Kansas',
        'KY': 'Kentucky',
        'LA': 'Louisiana',
        'ME': 'Maine',
        'MD': 'Maryland',
        'MA': 'Massachusetts',
        'MI': 'Michigan',
        'MN': 'Minnesota',
        'MS': 'Mississippi',
        'MO': 'Missouri',
        'MT': 'Montana',
        'NE': 'Nebraska',
        'NV': 'Nevada',
        'NH': 'New Hampshire',
        'NJ': 'New Jersey',
        'NM': 'New Mexico',
        'NY': 'New York',
        'NC': 'North Carolina',
        'ND': 'North Dakota',
        'OH': 'Ohio',
        'OK': 'Oklahoma',
        'OR': 'Oregon',
        'PA': 'Pennsylvania',
        'RI': 'Rhode Island',
        'SC': 'South Carolina',
        'SD': 'South Dakota',
        'TN': 'Tennessee',
        'TX': 'Texas',
        'UT': 'Utah',
        'VT': 'Vermont',
        'VA': 'Virginia',
        'WA': 'Washington',
        'WV': 'West Virginia',
        'WI': 'Wisconsin',
        'WY': 'Wyoming',
        // Canadian provinces
        'ON': 'Ontario',
        'BC': 'British Columbia',
        'AB': 'Alberta',
        'SK': 'Saskatchewan',
        'MB': 'Manitoba',
        'QC': 'Quebec',
        'NB': 'New Brunswick',
        'NS': 'Nova Scotia',
        'PE': 'Prince Edward Island',
        'NL': 'Newfoundland and Labrador',
        'NT': 'Northwest Territories',
        'NU': 'Nunavut',
        'YT': 'Yukon'
      }

      return abbrToFullName[abbreviation.toUpperCase()] || null
    },
    handleFocus () {
      // Ensure clean state when focusing on search bar
      this.selectedIndex = -1
      // Open mobile overlay when search bar is focused on mobile
      if (this.isMobile) {
        this.showMobileOverlay = true
        this.$nextTick(() => {
          this.$refs.mobileSearchInput.focus()
        })
      } else {
        this.showDropdown = true
        // Re-trigger search if there's existing query to show results
        if (this.searchQuery.length > 0) {
          this.handleSearch()
        }
      }
    },
    selectEventMobile (event) {
      this.searchQuery = event.name
      this.showMobileOverlay = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', event)
    },
    exploreAllShowsMobile () {
      this.searchQuery = ''
      this.showMobileOverlay = false
      // Navigate to home page which shows all upcoming shows
      this.$router.push('/')
    },
    exploreAllSeriesMobile () {
      this.searchQuery = ''
      this.showMobileOverlay = false
      // Navigate to show series page
      this.$router.push('/show-series')
    },
    closeMobileOverlay () {
      this.showMobileOverlay = false
      this.searchQuery = ''
      this.filteredEvents = []
      this.filteredSeries = []
      this.selectedIndex = -1 // Reset selection
      this.activeTab = 'shows' // Reset to default tab
    },
    selectSeriesMobile (seriesItem) {
      this.searchQuery = seriesItem.name
      this.showMobileOverlay = false
      this.selectedIndex = -1 // Reset keyboard selection
      this.$emit('select', seriesItem)
    },
    getSeriesLogo (seriesItem) {
      // Check for series logo using the pattern from ShowSeriesList.vue
      if (seriesItem.series_details && seriesItem.series_details.logo_id) {
        return cldUrl(seriesItem.series_details.logo_id, {h: 120})
      }

      // Fallback to a simple default logo
      return 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    handleSeriesImageError (e) {
      // Fallback to a simple default image if the series logo fails to load
      e.target.src = 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    getEventStatusBadge (event) {
      // Only show status badge for admin users and if status is not PUBLISHED
      if (!this.isAdminUser || !event.status || event.status === 'PUBLISHED' || event.status === 'ARCHIVED') {
        return null
      }

      const statusConfig = {
        'NEW': {text: 'New', class: 'bg-yellow-100 text-yellow-800 border-yellow-200'},
        'SETUP_COMPLETE': {text: 'Setup Complete', class: 'bg-blue-100 text-blue-800 border-blue-200'},
        'PUBLISH_REQUESTED': {text: 'Publish Requested', class: 'bg-green-100 text-green-800 border-green-200'},
        'CANCELLED': {text: 'Cancelled', class: 'bg-red-100 text-red-800 border-red-200'}
      }

      return statusConfig[event.status] || {text: event.status, class: 'bg-gray-100 text-gray-800 border-gray-200'}
    }
  }
}
</script>

<style scoped>
/* Ensure dropdown positioning */
.absolute {
  left: 0;
  right: auto;
}

/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
